import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { useSLAStatus } from '../../hooks/useAutomation';

export function SLASummaryCard() {
  const { data: slaStatus, isLoading, error } = useSLAStatus();

  if (isLoading) {
    return (
      <div className="card p-5">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="space-y-3">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-8 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !slaStatus) {
    return null;
  }

  const { ok_count, warning_count, escalated_count, total_assigned } = slaStatus;
  const hasIssues = warning_count > 0 || escalated_count > 0;

  return (
    <div className={`card p-5 ${hasIssues ? 'border-l-4 border-l-yellow-500' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-brand-primary" />
          <h3 className="font-semibold text-font-primary">SLA Status</h3>
        </div>
        {hasIssues && (
          <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
            {warning_count + escalated_count} need attention
          </span>
        )}
      </div>

      <div className="space-y-3">
        {/* On Track */}
        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-800">On Track</span>
          </div>
          <span className="text-lg font-semibold text-green-600">{ok_count}</span>
        </div>

        {/* Warning */}
        <Link
          to="/leads?sla_status=warning"
          className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">Warning</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-yellow-600">{warning_count}</span>
            {warning_count > 0 && <ArrowRight className="w-4 h-4 text-yellow-600" />}
          </div>
        </Link>

        {/* Escalated */}
        <Link
          to="/leads?sla_status=escalated"
          className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-sm font-medium text-red-800">Escalated</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-red-600">{escalated_count}</span>
            {escalated_count > 0 && <ArrowRight className="w-4 h-4 text-red-600" />}
          </div>
        </Link>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-font-secondary">
          <span>Total Assigned Leads</span>
          <span className="font-medium">{total_assigned}</span>
        </div>
      </div>
    </div>
  );
}
